import { api, type CustomerDto, type DocumentDto } from '../lib/api';
import { localStore, type FailedSyncEvent, type OutboxOp } from '../db';
import { encryptJson, decryptJson } from '../../../core/crypto';
import { refreshAccessToken } from '../../../core/lib/api';
import { compareByTitle } from '../lib/sort';
import { STALE_PENDING_MS } from '@dragonhq/protocol';
// Cyclic import: yjsCollab imports `pullAll` + `DocumentPlain` from here.
// The helpers below are invoked only inside function bodies (never at
// module-init time), so the cycle resolves fine — by the time any code
// calls upsertDocument or pullAll, both modules are fully loaded.
import {
  applyLatestIdbStateToLoadedDoc,
  getLiveBodySnapshot,
  getLoadedDocVersion,
  isDocLoaded,
  isSnapshotInFlight,
} from '../lib/yjsCollab';

export interface CustomerPlain {
  name: string;
  color: string; // hex
  /** Icon key from APP_ICONS. Rendered in the customer's colour. */
  icon?: string;
  tags?: string[];
}

export interface DocumentPlain {
  title: string;
  markdown: string;
  tags?: string[];
  /** Icon key from APP_ICONS. Only meaningful for top-level pages (not tabs). */
  icon?: string;
  /** Hex colour. Used for tabs; ignored for top-level pages. */
  color?: string;
  yjsState?: string; // base64 of Yjs doc for CRDT merging (future)
}

type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
function notify() {
  for (const l of listeners) l();
}

/**
 * Pulls the full state from the server and stores it (still encrypted) locally.
 * Called on unlock + periodically + on WS change events.
 *
 * Includes soft-deleted items so the local cache learns about deletions — a
 * prior version only fetched live rows, which left ghost entries in the
 * sidebar of any browser that had loaded the doc before another user deleted
 * it (the local row never got its deletedAt updated).
 */
export async function pullAll(): Promise<void> {
  const [custRes, docRes] = await Promise.all([
    api.listCustomers({ includeDeleted: true }),
    api.listDocuments({ includeDeleted: true }),
  ]);
  const now = new Date().toISOString();

  // Read local rows up front so we can compare updatedAt + pendingSync
  // against each server row. Without this, a pullAll that lands while an
  // in-progress snapshot PUT is still flying sees the stale server row
  // and overwrites the `pendingSync:true` local row that `persistSnapshot`
  // just wrote — next `acquireDoc` then seeds from the clobbered state
  // and the user's edits are silently lost.
  const [localCustList, localDocList] = await Promise.all([
    localStore.listCustomers(),
    localStore.listDocuments(),
  ]);
  const localCustMap = new Map(localCustList.map((r) => [r.id, r] as const));
  const localDocMap = new Map(localDocList.map((r) => [r.id, r] as const));

  const nowMs = Date.now();
  const serverCustIds = new Set<string>();
  for (const c of custRes.customers) {
    serverCustIds.add(c.id);
    const local = localCustMap.get(c.id);
    if (local?.pendingSync && local.updatedAt > c.updatedAt) {
      // Local has an unsynced write that's newer than the server row.
      // Honour the in-flight write — UNLESS the row has been pending
      // longer than STALE_PENDING_MS (60 s). The retry loop in
      // yjsCollab.persistSnapshot caps backoff at 30 s and surfaces
      // permanent failures, so anything pending past this window is a
      // ghost from a previous session that should yield to server reality.
      const ageMs = nowMs - new Date(local.updatedAt).getTime();
      if (ageMs < STALE_PENDING_MS) continue;
      console.warn('[sync] stale pending customer yielded to server', { id: c.id, ageMs });
    }
    await localStore.putCustomer({
      id: c.id,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      deletedAt: c.deletedAt,
      pendingSync: false,
      data: c,
    });
  }
  const serverDocIds = new Set<string>();
  for (const d of docRes.documents) {
    serverDocIds.add(d.id);
    // Belt: a snapshot PUT is actively in flight — local row is the freshest
    // representation the client has. Braces: local updatedAt exceeds server
    // updatedAt AND local is pendingSync within the staleness window.
    if (isSnapshotInFlight(d.id)) continue;
    const local = localDocMap.get(d.id);
    if (local?.pendingSync && local.updatedAt > d.updatedAt) {
      const ageMs = nowMs - new Date(local.updatedAt).getTime();
      if (ageMs < STALE_PENDING_MS) continue;
      console.warn('[sync] stale pending document yielded to server', { id: d.id, ageMs });
    }
    await localStore.putDocument({
      id: d.id,
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
      deletedAt: d.deletedAt,
      pendingSync: false,
      data: d,
    });
    // F2 catch-up: if a Y.Doc for this id is loaded in memory and its
    // tracked version is behind the row we just wrote, re-apply the new
    // ciphertext via the CRDT-safe path. Without this, a Y.Doc retained
    // across a long idle (or whose peer ran a snapshot we never received
    // via WS) keeps showing stale content until a manual Resync.
    if (isDocLoaded(d.id)) {
      const loadedVersion = getLoadedDocVersion(d.id);
      if (loadedVersion === undefined || loadedVersion < d.payload.version) {
        await applyLatestIdbStateToLoadedDoc(d.id);
      }
    }
  }

  // Drop local rows the server doesn't know about. Skip pendingSync rows —
  // those represent unsynced local writes that the outbox will push next.
  for (const row of localCustList) {
    if (!serverCustIds.has(row.id) && !row.pendingSync) {
      await localStore.deleteCustomer(row.id);
    }
  }
  for (const row of localDocList) {
    if (!serverDocIds.has(row.id) && !row.pendingSync) {
      await localStore.deleteDocument(row.id);
    }
  }

  await localStore.setMeta('lastSync', now);
  notify();
}

/**
 * Permanent-error emitter for outbox ops. 404 (already deleted), 401 (auth
 * lost), 403 (role revoked), 410 (gone) — none of these will recover by
 * retrying, so the op is dropped and the user is notified via this listener.
 */
export interface PermanentOutboxError {
  op: OutboxOp;
  status: number;
  message: string;
}
const outboxPermanentListeners = new Set<(e: PermanentOutboxError) => void>();
export function onPermanentOutboxError(cb: (e: PermanentOutboxError) => void): () => void {
  outboxPermanentListeners.add(cb);
  return () => outboxPermanentListeners.delete(cb);
}
function emitOutboxPermanent(e: PermanentOutboxError): void {
  // Persist a breadcrumb so the status light stays red (and the user can
  // open Recover-failed-edits) until acknowledged. Fire-and-forget — the
  // toast is the synchronous signal; this is the durable one. See plan F4.
  const event: FailedSyncEvent = {
    eventId: `outbox-${e.op.opId ?? `${e.op.type}-${e.op.id}-${Date.now()}`}`,
    source: 'outbox',
    opType: e.op.type,
    isNew: 'isNew' in e.op ? (e.op as any).isNew : undefined,
    documentId: e.op.type.startsWith('upsert_document') || e.op.type === 'delete_document' ? e.op.id : undefined,
    customerId:
      e.op.type === 'upsert_document'
        ? (e.op as any).customerId
        : e.op.type === 'upsert_customer' || e.op.type === 'delete_customer'
          ? e.op.id
          : undefined,
    status: e.status,
    message: e.message,
    at: new Date().toISOString(),
  };
  void localStore
    .appendFailedEvent(event)
    .catch((err) => console.warn('[sync] appendFailedEvent failed', err));
  for (const l of outboxPermanentListeners) {
    try { l(e); } catch (err) { console.warn('[sync] outbox-permanent listener threw', err); }
  }
}

function isOutboxPermanentError(err: any): boolean {
  const s = err?.status;
  // 400 schema rejection / 404 gone / 403 authz / 410 deleted — retrying never recovers.
  // 409 is treated as transient for upserts (caller reconciles via pullAll).
  if (s === 400 || s === 404 || s === 403 || s === 410) return true;
  // 401 is split: only `session_revoked` (admin cutoff / explicit logout) is
  // definitive. Bare `unauthorized` is usually a freshly-expired token or a
  // transient captive-portal/MITM blip and must NOT silently drop the op —
  // see plan F3. Such errors fall through to the transient retry path.
  if (s === 401 && err?.body?.error === 'session_revoked') return true;
  return false;
}

/**
 * Ceiling on transient retries. With the 30 s flush timer + reconnect/online
 * triggers, an op that genuinely can't land (server wedged, op poisoned in
 * some way the 4xx classifier misses) gets retried every ~30 s; 100 attempts
 * is roughly an hour of stuck-ness — comfortably long enough to ride out a
 * normal outage but short enough that the user gets a clear "this is broken,
 * stop staring at the spinner" message instead of silent forever-retry.
 */
const MAX_OUTBOX_ATTEMPTS = 100;

/**
 * Drain queued ops in order. Halts on the first transient failure (network
 * / 5xx / 409) so per-doc ordering is preserved across retries — the next
 * `flushOutbox` call (on reconnect, on a 30 s timer, on the next mutation)
 * will resume from the same op. Permanent failures (404/401/403/410) drop
 * the op and emit a user-facing notification rather than blocking the queue
 * forever.
 *
 * Each transient failure bumps `attemptCount` and stamps `lastError` on the
 * op so the sync-status UI can show "Saving 3 edits — last attempt: 503".
 */
export async function flushOutbox(): Promise<void> {
  const ops = await localStore.listOps();
  // 401-recovery: a single 401 across the whole drain can be the JWT
  // ticking past expiry on a long-idle tab. We refresh ONCE and re-try
  // the op that triggered it; subsequent 401s in the same drain are
  // treated as genuinely revoked and drop the op via the permanent path.
  let refreshAttemptedThisDrain = false;
  for (const op of ops) {
    try {
      await applyOp(op);
      if (op.opId) await localStore.removeOp(op.opId);
    } catch (err: any) {
      if (err?.status === 401 && !refreshAttemptedThisDrain) {
        refreshAttemptedThisDrain = true;
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Retry this op once with the fresh token. If that also 401s
          // we'll fall through to the permanent path on the next loop.
          try {
            await applyOp(op);
            if (op.opId) await localStore.removeOp(op.opId);
            continue;
          } catch (retryErr: any) {
            err = retryErr;
          }
        }
      }
      if (isOutboxPermanentError(err)) {
        console.warn('[sync] op permanently failed, dropping', op, err);
        if (op.opId) {
          try { await localStore.removeOp(op.opId); } catch { /* non-fatal */ }
        }
        emitOutboxPermanent({
          op,
          status: err?.status ?? 0,
          message: err?.body?.error ?? err?.message ?? 'Permanent outbox failure',
        });
        // Continue draining; subsequent ops aren't necessarily blocked by this one.
        continue;
      }
      // Transient — stamp metadata, halt to preserve order. Boot/reconnect/timer flush retries.
      const nextAttempt = (op.attemptCount ?? 0) + 1;
      const errMsg = err?.body?.error ?? err?.message ?? String(err?.status ?? err);
      if (nextAttempt >= MAX_OUTBOX_ATTEMPTS) {
        console.warn('[sync] op exceeded max retry attempts, dropping', op, err);
        if (op.opId) {
          try { await localStore.removeOp(op.opId); } catch { /* non-fatal */ }
        }
        emitOutboxPermanent({
          op,
          status: err?.status ?? 0,
          message: `Stuck after ${nextAttempt} attempts (${errMsg}) — contact support if this persists.`,
        });
        // Drop and continue draining — a single stuck op shouldn't poison the queue.
        continue;
      }
      const updated: OutboxOp = {
        ...op,
        attemptCount: nextAttempt,
        lastError: errMsg,
        lastAttemptAt: new Date().toISOString(),
      };
      if (op.opId) {
        try { await localStore.updateOp(updated); } catch { /* non-fatal */ }
      }
      console.warn('[sync] op failed, will retry', op, err);
      notify();
      return;
    }
  }
  notify();
}

async function applyOp(op: OutboxOp) {
  if (op.type === 'upsert_customer') {
    if (op.isNew) await api.createCustomer(op.id, op.payload as any);
    else await api.updateCustomer(op.id, op.payload as any);
  } else if (op.type === 'delete_customer') {
    await api.deleteCustomer(op.id);
  } else if (op.type === 'upsert_document') {
    if (op.isNew) {
      await api.createDocument({
        id: op.id,
        customerId: op.customerId,
        parentId: op.parentId,
        payload: op.payload as any,
      });
    } else {
      await api.updateDocument(op.id, {
        customerId: op.customerId,
        parentId: op.parentId,
        payload: op.payload as any,
      });
    }
  } else if (op.type === 'delete_document') {
    await api.deleteDocument(op.id);
  }
}

/**
 * Snapshot of pending sync state for the UI. Drives the visible
 * "Saved / Saving N edit(s) / Offline — N queued" indicator.
 */
export async function getPendingCounts(): Promise<{
  pendingDocCount: number;
  pendingCustomerCount: number;
  outboxOpCount: number;
}> {
  const [docs, custs, ops] = await Promise.all([
    localStore.listDocuments(),
    localStore.listCustomers(),
    localStore.listOps(),
  ]);
  return {
    pendingDocCount: docs.filter((d) => d.pendingSync).length,
    pendingCustomerCount: custs.filter((c) => c.pendingSync).length,
    outboxOpCount: ops.length,
  };
}

// ===== high-level mutations used by the UI =====

export async function upsertCustomer(
  id: string,
  plain: CustomerPlain,
  key: CryptoKey,
  isNew: boolean,
) {
  const payload = await encryptJson(plain, key);
  const now = new Date().toISOString();
  await localStore.putCustomer({
    id,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    pendingSync: true,
    data: { id, payload, deletedAt: null, createdAt: now, updatedAt: now },
  });
  await localStore.enqueueOp({ type: 'upsert_customer', id, payload, isNew });
  notify();
  void flushOutbox();
}

export async function deleteCustomer(id: string) {
  const row = await localStore.getCustomer(id);
  if (row) {
    row.deletedAt = new Date().toISOString();
    row.pendingSync = true;
    await localStore.putCustomer(row);
  }
  await localStore.enqueueOp({ type: 'delete_customer', id });
  notify();
  void flushOutbox();
}

export async function upsertDocument(
  id: string,
  customerId: string,
  parentId: string | null,
  plain: DocumentPlain,
  key: CryptoKey,
  isNew: boolean,
) {
  // Body-preservation invariant: every `isNew=false` upsertDocument call
  // replaces `documents.payload` wholesale AND appends a `document_versions`
  // row. If the caller didn't carry the current body (yjsState + markdown),
  // the server's body is erased and a fresh empty-body version row is
  // written. Call sites that only intend a meta update (rename, icon
  // auto-suggest, attachment drop) don't set yjsState — so without this
  // merge they silently wipe every edit.
  //
  // Source-of-truth preference: live Y.Doc (authoritative while loaded) →
  // existing IndexedDB row (last persisted state) → caller's values as-is.
  const merged: DocumentPlain = { ...plain };
  if (!isNew) {
    const live = getLiveBodySnapshot(id);
    if (live) {
      merged.yjsState = live.yjsState;
      // Live derived markdown is refreshed on every editor transaction —
      // strictly newer than any caller-provided markdown when the doc is
      // loaded. Drop handlers for an open page go through the editor's own
      // drop path (not this code), so there's no legitimate "caller knows
      // better" case when a live Y.Doc exists.
      merged.markdown = live.markdown;
    } else {
      try {
        const existing = await localStore.getDocument(id);
        if (existing) {
          const existingPlain = await decryptJson<DocumentPlain>(
            existing.data.payload,
            key,
          );
          if (merged.yjsState === undefined) {
            merged.yjsState = existingPlain.yjsState;
          }
          // Only fall back to existing markdown when the caller didn't
          // provide one. Attachment-drop handlers build their own updated
          // markdown and must not be overridden.
          if (!merged.markdown && existingPlain.markdown) {
            merged.markdown = existingPlain.markdown;
          }
        }
      } catch {
        /* corrupted existing payload — leave merged as-is */
      }
    }
  }
  const payload = await encryptJson(merged, key);
  const now = new Date().toISOString();
  await localStore.putDocument({
    id,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    pendingSync: true,
    data: {
      id,
      customerId,
      parentId,
      payload,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
    },
  });
  await localStore.enqueueOp({
    type: 'upsert_document',
    id,
    customerId,
    parentId,
    payload,
    isNew,
  });
  notify();
  void flushOutbox();
}

export async function deleteDocument(id: string) {
  const row = await localStore.getDocument(id);
  if (row) {
    row.deletedAt = new Date().toISOString();
    row.pendingSync = true;
    await localStore.putDocument(row);
  }
  await localStore.enqueueOp({ type: 'delete_document', id });
  notify();
  void flushOutbox();
}

// ===== decrypted-view helpers =====

export interface DecryptedCustomer extends CustomerPlain {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  pendingSync: boolean;
}

export interface DecryptedDocument extends DocumentPlain {
  id: string;
  customerId: string;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  pendingSync: boolean;
  lock?: DocumentDto['lock'];
}

export async function listDecryptedCustomers(key: CryptoKey): Promise<DecryptedCustomer[]> {
  const rows = await localStore.listCustomers();
  const result: DecryptedCustomer[] = [];
  for (const r of rows) {
    if (r.deletedAt) continue;
    try {
      const plain = await decryptJson<CustomerPlain>(r.data.payload, key);
      result.push({
        ...plain,
        id: r.id,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        deletedAt: r.deletedAt,
        pendingSync: r.pendingSync,
      });
    } catch {
      // skip corrupted rows
    }
  }
  result.sort(compareByTitle<DecryptedCustomer>((c) => c.name));
  return result;
}

export async function listDecryptedDocuments(customerId: string, key: CryptoKey): Promise<DecryptedDocument[]> {
  const rows = await localStore.listDocumentsByCustomer(customerId);
  const result: DecryptedDocument[] = [];
  for (const r of rows) {
    if (r.deletedAt) continue;
    try {
      const plain = await decryptJson<DocumentPlain>(r.data.payload, key);
      result.push({
        ...plain,
        id: r.id,
        customerId: r.data.customerId,
        parentId: r.data.parentId,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        deletedAt: r.deletedAt,
        pendingSync: r.pendingSync,
        lock: r.data.lock,
      });
    } catch {
      // skip
    }
  }
  result.sort(compareByTitle<DecryptedDocument>((d) => d.title));
  return result;
}

export async function getDecryptedDocument(id: string, key: CryptoKey): Promise<DecryptedDocument | null> {
  const r = await localStore.getDocument(id);
  if (!r) return null;
  try {
    const plain = await decryptJson<DocumentPlain>(r.data.payload, key);
    return {
      ...plain,
      id: r.id,
      customerId: r.data.customerId,
      parentId: r.data.parentId,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
      deletedAt: r.deletedAt,
      pendingSync: r.pendingSync,
      lock: r.data.lock,
    };
  } catch {
    return null;
  }
}
